import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Database } from '@/integrations/supabase/types';
import { OrderStatus } from '@/types/order';
import { Trash2, Plus, Minus, ShoppingCart, Loader2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AnimatedBackground from '@/components/ui/animated-background';

type FloristProfile = Database['public']['Tables']['florist_profiles']['Row'];
type OrderInsert = Database['public']['Tables']['orders']['Insert'];
type OrderItemInsert = Database['public']['Tables']['order_items']['Insert'];

interface CartItem {
  product_id: string;
  product_name: string;
  florist_id: string;
  quantity: number;
  unit_price: number;
  main_image_url: string;
  customizations?: {
    [key: string]: {
      option: string;
      price_adjustment: number;
    };
  };
}

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export default function Cart() {
  const { items, removeItem, updateQuantity, clear: clearCart, total, itemCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const subtotal = total;
  const deliveryFee = 0;

  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity < 1) return;
    updateQuantity(id, quantity);
  };

  const handleRemoveItem = (id: string) => {
    removeItem(id);
  };

  const handleCheckout = () => {
    if (!items || items.length === 0) {
      toast({
        title: 'Empty Cart',
        description: 'Your cart is empty. Please add items before proceeding.',
        variant: 'destructive',
      });
      return;
    }
    // Generate a temporary order ID for the delivery details page
    const tempOrderId = `temp_${Date.now()}`;
    navigate(`/delivery-details/${tempOrderId}`);
  };

  const handleCreateAccount = () => {
    // Store the current cart state in session storage
    sessionStorage.setItem('checkout_redirect', '/delivery');
    navigate('/auth?tab=customer&redirect=/delivery'); // ✅ FIXED: Use correct auth route
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-brand-accent">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary mx-auto mb-4" />
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="min-h-screen bg-brand-accent">
        <div className="container mx-auto px-4 pt-24 sm:pt-20 pb-4 sm:pb-12 flex items-center justify-center min-h-[calc(100vh-180px)]">
          <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 border border-brand-primary/10">
            <ShoppingBag className="h-12 w-12 mx-auto text-brand-primary/30 mb-4" />
            <h1 className="text-2xl font-semibold mb-4 text-brand-secondary text-center">Your cart is empty</h1>
            <p className="text-brand-secondary/70 mb-8 text-center">Browse our beautiful flower arrangements</p>
            <Button 
              onClick={() => navigate("/")}
              className="w-full bg-[#967F81] text-white hover:bg-[#843329]"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-accent">
      <div className="container mx-auto px-4 pt-24 sm:pt-20 pb-12">
        <h1 className="text-2xl font-bold mb-8 text-brand-secondary">Shopping Cart</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {items.map(item => (
              <div key={item.id} className="flex gap-4 py-4 border-b border-brand-primary/10 bg-white rounded-lg mb-4 p-4 shadow-sm">
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-24 h-24 object-cover rounded"
                  />
                )}
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <h3 className="font-medium text-brand-secondary">{item.title}</h3>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleQuantityChange(item.id, Math.max(1, item.quantity - 1))}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="font-medium text-brand-secondary">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm h-fit border border-brand-primary/10">
            <h2 className="text-lg font-semibold mb-4 text-brand-secondary">Order Summary</h2>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Delivery Fee</span>
                <span>Calculated at checkout</span>
              </div>
            </div>

            {user ? (
              <Button
                className="w-full bg-[#967F81] hover:bg-[#843329] text-white mt-6"
                onClick={handleCheckout}
                disabled={isLoading}
              >
                Proceed to Checkout
              </Button>
            ) : (
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border bg-card p-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <h4 className="font-medium">Create an Account for Better Experience</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Sign up to enjoy these benefits:
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                        <li>• Track your order status</li>
                        <li>• View order history</li>
                        <li>• Save delivery addresses</li>
                        <li>• Faster checkout next time</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleCheckout}
                    >
                      Continue as Guest
                    </Button>
                    <Button
                      className="flex-1 bg-[#967F81] text-white hover:bg-[#843329]"
                      onClick={handleCreateAccount}
                    >
                      Create Account
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}